import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { funnelsApi } from '../lib/api.js';
import type { FunnelDefinition } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { ComingSoon } from '../components/ComingSoon.js';

/**
 * @graph-node {Page}
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル一覧ページ。登録/購入/返却/指名/診断等のコンバージョンファネル定義カードを表示
 * @graph-connects funnelsApi [calls] Funnels APIクライアント呼び出し
 * @graph-connects queryKeys [calls] クエリキー生成
 * @graph-connects ComingSoon [calls] Coming Soonオーバーレイ描画
 */
export const Route = createFileRoute('/funnels/')({
  component: FunnelsPage,
});

// ============================================================
// ファネルカード
// ============================================================

/** @graph-connects none */
function FunnelCard({ funnel }: { funnel: FunnelDefinition }) {
  const { t } = useTranslation();
  const funnelLabel = t(`funnels.funnelNames.${funnel.id}`, funnel.name);
  const enabledFunnels = ['registration', 'purchase'];
  const isComingSoon = !enabledFunnels.includes(funnel.id);

  const card = (
    <Link
      to="/funnels/$funnelId"
      params={{ funnelId: funnel.id }}
      className="card hover:shadow-lg transition-shadow group block"
      onClick={
        isComingSoon
          ? (e: React.MouseEvent) => {
              e.preventDefault();
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-accent-primary)] transition-colors">
          {funnelLabel}
        </h3>
        <span className="text-xs text-[var(--color-text-muted)]">
          {funnel.steps.length} {t('funnels.steps')}
        </span>
      </div>
      <p className="text-xs text-[var(--color-text-secondary)] mb-4 line-clamp-2">
        {funnel.description}
      </p>

      {/* ステップフロー（簡易表示） */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {funnel.steps.map((step, idx) => (
          <div key={step.name} className="flex items-center gap-1 flex-shrink-0">
            <span className="px-2 py-0.5 rounded bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)] text-[10px] font-medium">
              {step.name}
            </span>
            {idx < funnel.steps.length - 1 && (
              <svg
                className="w-3 h-3 text-[var(--color-text-muted)]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between">
        <span className="text-xs text-[var(--color-text-muted)]">
          {funnel.analysisTypes.length} analysis types
        </span>
        <span className="text-xs text-[var(--color-accent-primary)] group-hover:translate-x-0.5 transition-transform">
          {t('funnels.viewDetail')} →
        </span>
      </div>
    </Link>
  );

  if (isComingSoon) {
    return <ComingSoon>{card}</ComingSoon>;
  }

  return card;
}

// ============================================================
// メインページ
// ============================================================

/** @graph-connects ux-insights-api [calls] UX Insights APIを呼び出す */
function FunnelsPage() {
  const { t } = useTranslation();

  const funnelsQuery = useQuery({
    queryKey: queryKeys.funnels.list(),
    queryFn: () => funnelsApi.getList(),
    staleTime: 30 * 60 * 1000,
  });

  return (
    <div className="space-y-6">
      {/* ページヘッダー */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('funnels.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t('funnels.description')}</p>
      </div>

      {/* ファネル一覧 */}
      {funnelsQuery.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="card">
              <div className="skeleton-shimmer h-5 w-32 mb-3" />
              <div className="skeleton-shimmer h-3 w-full mb-4" />
              <div className="skeleton-shimmer h-6 w-full mb-3" />
              <div className="skeleton-shimmer h-4 w-24" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {funnelsQuery.data?.data.map((funnel) => (
            <FunnelCard key={funnel.id} funnel={funnel} />
          ))}
        </div>
      )}
    </div>
  );
}
